import { useLayoutEffect, useState, type RefObject } from "react";

/** Matches `$cc-nav-mega-panel-gutter / 2` in `_nav-services-menu.scss`. */
export const CC_NAV_MEGA_VIEWPORT_PAD_PX = 16;

const CC_NAV_DESKTOP_MQ = "(min-width: 811px)";

export function useCcNavMegaMountEl(
  megaMountRef?: RefObject<HTMLElement | null>,
): HTMLElement | null {
  const [mountEl, setMountEl] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!megaMountRef) {
      setMountEl(null);
      return;
    }
    setMountEl(megaMountRef.current);
  });

  return megaMountRef ? mountEl : null;
}

type ApplyCcNavMegaPanelPositionResult = {
  alignEnd: boolean;
};

/** Positions a pill-mounted mega panel; returns whether end-alignment was applied. */
export function applyCcNavMegaPanelPosition(
  mountEl: HTMLElement,
  triggerEl: HTMLElement,
  panelEl: HTMLElement,
  preferAlignEnd = false,
): ApplyCcNavMegaPanelPositionResult {
  const mountRect = mountEl.getBoundingClientRect();
  const triggerRect = triggerEl.getBoundingClientRect();
  const panelWidth = panelEl.offsetWidth;
  const viewportMin = CC_NAV_MEGA_VIEWPORT_PAD_PX;
  const viewportMax = window.innerWidth - CC_NAV_MEGA_VIEWPORT_PAD_PX;

  const topPx = triggerRect.bottom - mountRect.top;
  let leftPx = triggerRect.left - mountRect.left;

  let alignEnd = false;
  if (preferAlignEnd) {
    const endAlignedLeft = triggerRect.right - mountRect.left - panelWidth;
    const endAlignedRight = triggerRect.right;
    if (
      endAlignedRight > viewportMax &&
      endAlignedLeft > viewportMin
    ) {
      leftPx = endAlignedLeft;
      alignEnd = true;
    }
  }

  let offsetX = 0;
  const panelLeftInViewport = mountRect.left + leftPx;
  const overflowRight = panelLeftInViewport + panelWidth - viewportMax;
  if (overflowRight > 0) {
    offsetX -= overflowRight;
  }
  const overflowLeft = panelLeftInViewport + offsetX - viewportMin;
  if (overflowLeft < 0) {
    offsetX -= overflowLeft;
  }

  leftPx += offsetX;

  panelEl.style.setProperty("--cc-nav-mega-panel-top", `${topPx}px`);
  panelEl.style.setProperty("--cc-nav-mega-panel-left", `${leftPx}px`);
  panelEl.style.removeProperty("--cc-nav-mega-panel-x");

  return { alignEnd };
}

export function clearCcNavMegaPanelPosition(panelEl: HTMLElement | null) {
  if (!panelEl) return;
  panelEl.style.removeProperty("--cc-nav-mega-panel-top");
  panelEl.style.removeProperty("--cc-nav-mega-panel-left");
  panelEl.style.removeProperty("--cc-nav-mega-panel-x");
}

export function useCcNavMegaPanelPosition(
  open: boolean,
  megaMountEl: HTMLElement | null,
  triggerRef: RefObject<HTMLElement | null>,
  panelRef: RefObject<HTMLElement | null>,
  deps: readonly unknown[],
  preferAlignEnd = false,
): boolean {
  const [alignEnd, setAlignEnd] = useState(false);

  useLayoutEffect(() => {
    const desktopMq = window.matchMedia(CC_NAV_DESKTOP_MQ);
    const triggerEl = triggerRef.current;
    const panelEl = panelRef.current;
    if (!open || !desktopMq.matches || !megaMountEl || !triggerEl || !panelEl) {
      // Keep the last top/left while closing so the pill-mounted panel does not
      // snap to var(*, 0) during the opacity/visibility transition.
      if (open) {
        clearCcNavMegaPanelPosition(panelEl);
      }
      setAlignEnd(false);
      return;
    }

    const position = () => {
      const liveTrigger = triggerRef.current;
      const livePanel = panelRef.current;
      if (!megaMountEl || !liveTrigger || !livePanel) return;
      const result = applyCcNavMegaPanelPosition(
        megaMountEl,
        liveTrigger,
        livePanel,
        preferAlignEnd,
      );
      setAlignEnd(result.alignEnd);
    };

    position();
    window.addEventListener("resize", position);
    return () => window.removeEventListener("resize", position);
  }, [open, megaMountEl, triggerRef, panelRef, preferAlignEnd, ...deps]);

  return alignEnd;
}
